import React from 'react'

/**
 * A swipeable component that allows you to swipe a component left or right to trigger a callback.
 * It uses touch events for mobile and mouse events for desktop.
 *
 * @param  children - The content of the swipeable component.
 * @param  className - Additional class names to add to the wrapper div.
 * @param  onSwipeLeft - Callback function to be called when swiped left. If not provided, you cannot swipe left.
 * @param  swipeLeftIndicator - A component to show when swiped left. If not provided, nothing is shown.
 * @param  onSwipeRight - Callback function to be called when swiped right. If not provided, you cannot swipe right.
 * @param  swipeRightIndicator - A component to show when swiped right. If not provided, nothing is shown.
 * @param  swipeThreshold - The threshold in pixels to trigger a swipe. Default is 100.
 * @param  onClick - Callback function to be called when the component is clicked.
 */
export const Swipeable: React.FC<{
  children: React.ReactNode
  className?: string
  onSwipeLeft?: () => boolean | void
  swipeLeftIndicator?: React.ReactNode
  onSwipeRight?: () => boolean | void
  swipeRightIndicator?: React.ReactNode
  swipeThreshold?: number
  onClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void
}> = ({
  children,
  className,
  onClick,
  onSwipeLeft,
  swipeLeftIndicator,
  onSwipeRight,
  swipeRightIndicator,
  swipeThreshold = 100,
}) => {
  const [startX, setStartX] = React.useState<number | undefined>(undefined)
  const [startY, setStartY] = React.useState<number | undefined>(undefined)
  const [transform, setTransform] = React.useState<number>(0)
  const [deleted, setDeleted] = React.useState<boolean>(false)
  const [fullyDeleted, setFullyDeleted] = React.useState<boolean>(false)

  const start = (x: number, y: number) => {
    setStartX(x)
    setStartY(y)
  }

  const move = (x: number, y: number) => {
    if (deleted || startX === undefined || startY === undefined) return
    const diffX = x - startX
    const maxX = onSwipeRight ? swipeThreshold : 0
    const minX = onSwipeLeft ? -swipeThreshold : 0
    const clampedDiffX = Math.min(maxX, Math.max(minX, diffX))
    setTransform(clampedDiffX)
  }

  const cancel = () => {
    if (deleted) return
    setStartX(undefined)
    setStartY(undefined)
    setTransform(0)
  }

  const up = (e?: { preventDefault: () => void }) => {
    if (deleted || startX === undefined || startY === undefined) return
    if (Math.abs(transform) > 50 && e) e.preventDefault()
    if (transform >= swipeThreshold && onSwipeRight && onSwipeRight()) {
      // onSwipeLeft() returns true if the swipe should be considered successful and this message should animate off to the left
      setTransform(750)
      setDeleted(true)
    } else if (transform <= -swipeThreshold && onSwipeLeft && onSwipeLeft()) {
      setTransform(-750)
      setDeleted(true)
    } else {
      cancel()
    }
  }

  React.useEffect(() => {
    if (deleted)
      setTimeout(() => {
        setFullyDeleted(true)
      }, 200)
  }, [deleted])

  return (
    <div
      className={`relative ${className} ${fullyDeleted ? 'hidden' : ''}`}
      onTouchStart={(e) => {
        // e.preventDefault() breaks scrolling on mobile
        start(e.touches[0].clientX, e.touches[0].clientY)
      }}
      onMouseDown={(e) => {
        e.preventDefault()
        start(e.clientX, e.clientY)
      }}
      onMouseMove={(e) => move(e.clientX, e.clientY)}
      onMouseUp={up}
      onTouchMove={(e) => move(e.touches[0].clientX, e.touches[0].clientY)}
      onMouseLeave={(e) => {
        if (!startX) return
        if (Math.abs(e.clientX - startX) < swipeThreshold * 3) {
          // the threshold is larger if you just exit without mouseup, to avoid accidental deletes
          cancel()
        } else {
          up(e)
        }
      }}
      onTouchEnd={up}
      onTouchCancel={cancel}
      onClick={(e) => {
        if (!deleted && onClick) onClick(e)
      }}
      style={{
        transform: `translateX(${transform}px)`,
        transition: startX && !deleted ? 'none' : 'transform 0.2s',
      }}
    >
      {children}
      {swipeRightIndicator && (
        <div
          className={`absolute bottom-0 right-full top-0 ${transform >= swipeThreshold ? 'opacity-100' : 'opacity-0'} transition-opacity duration-150`}
          style={{ width: swipeThreshold }}
        >
          {swipeRightIndicator}
        </div>
      )}
      {swipeLeftIndicator && (
        <div
          className={`absolute bottom-0 left-full top-0 ${transform <= -swipeThreshold ? 'opacity-100' : 'opacity-0'} transition-opacity duration-150`}
          style={{ width: swipeThreshold }}
        >
          {swipeLeftIndicator}
        </div>
      )}
    </div>
  )
}
